export type BusinessPackageShowcase = {
  id: string;
  label: string;
  labelZh: string;
  patternLayer: 'foundation' | 'specific';
  summary: string;
  summaryZh: string;
  archetypePresetId: 'haulage' | 'warehouse' | 'finance_core';
  builderArtifactType:
    | 'bundle'
    | 'view-template'
    | 'reaction-rule'
    | 'virtual-endpoint'
    | 'semantic-model'
    | 'workspace-template'
    | 'report-model'
    | 'billing-model'
    | 'charge-event'
    | 'report-fact'
    | 'printable-view-model';
  builderPrompt: string;
  builderPromptZh: string;
  publishName: string;
  publishNameZh: string;
  outcome: string;
  outcomeZh: string;
  buildsOn?: string;
  demoSteps: string[];
  demoStepsZh: string[];
};

export const BUSINESS_PACKAGE_SHOWCASES: BusinessPackageShowcase[] = [
  {
    id: 'haulage-control-tower',
    label: 'Haulage Control Tower',
    labelZh: '拖车控制塔',
    patternLayer: 'specific',
    summary: 'Show how a tenant composes AI dispatch, ETA updates, and billing handoff without touching infra.',
    summaryZh: '演示租户如何不碰基础设施，直接组合 AI 派车、ETA 更新与计费交接能力。',
    archetypePresetId: 'haulage',
    builderArtifactType: 'bundle',
    builderPrompt: 'Build a haulage control tower mini app with dispatch planning board, customer ETA feed, driver exception alerts, and billing-ready workflow for completed trips.',
    builderPromptZh: '构建一个拖车控制塔迷你应用，包含派车看板、客户 ETA 动态、司机异常告警，以及完成行程后的 billing-ready 工作流。',
    publishName: 'Haulage Control Tower',
    publishNameZh: '拖车控制塔',
    outcome: 'A tenant-ready TMS overlay that demonstrates AI-native operations instead of CRUD screens.',
    outcomeZh: '一个可直接面向租户的 TMS 叠加层，重点展示 AI 原生运营，而不是传统 CRUD 页面。',
    demoSteps: [
      'Start from the haulage advisor blueprint.',
      'Answer operation questions for dispatch, ETA, and billing touchpoints.',
      'Jump to Builder with the prepared mini-app prompt.',
      'Publish and share the package to Exchange.',
    ],
    demoStepsZh: [
      '从 haulage 顾问蓝图开始。',
      '回答派车、ETA、计费触点等业务问题。',
      '带着准备好的 mini-app prompt 跳转到 Builder。',
      '发布并把 package 分享到 Exchange。',
    ],
  },
  {
    id: 'warehouse-foundation-runtime',
    label: 'Warehouse Foundation Runtime',
    labelZh: '仓库基础运行时',
    patternLayer: 'foundation',
    summary: 'Define the shared semantic truth for warehouse execution before generating tenant-specific workspaces or bundles.',
    summaryZh: '先定义仓库执行的共享语义真相，再去生成租户特定 workspace 或 bundle。',
    archetypePresetId: 'warehouse',
    builderArtifactType: 'semantic-model',
    builderPrompt: 'Create a warehouse foundation semantic model for Boox that maps canonical roles for job, arrival_unit, stock_header, stock_unit, warehouse_task, document, exception, charge_event, printable_view_model, and report_fact. Include aliases for bundle-heavy, lot-heavy, and container-heavy tenants so future tenant-specific workspaces can extend this foundation without recompiling.',
    builderPromptZh: '为 Boox 构建一个仓库基础 semantic model，把 job、arrival_unit、stock_header、stock_unit、warehouse_task、document、exception、charge_event、printable_view_model、report_fact 映射为 canonical roles，并同时覆盖 bundle-heavy、lot-heavy、container-heavy 三类租户别名，使后续租户特定 workspace 可以在不重编译的前提下扩展这套 foundation。',
    publishName: 'Warehouse Foundation Runtime',
    publishNameZh: '仓库基础运行时',
    outcome: 'A reusable warehouse foundation artifact that future tenant overlays can build on instead of branching core WMS code.',
    outcomeZh: '一个可复用的仓库 foundation artifact，后续租户 overlay 应建立在它之上，而不是继续分叉核心 WMS 代码。',
    demoSteps: [
      'Start from the warehouse foundation blueprint, not a tenant page mock.',
      'Generate and publish the semantic model as the shared execution truth.',
      'Use the published runtime artifact as the base contract for workspace, report, billing, and print layers.',
      'Only after the foundation is stable, continue into a tenant-specific overlay.',
    ],
    demoStepsZh: [
      '先从 warehouse foundation blueprint 开始，而不是直接画租户页面。',
      '生成并发布 semantic model，作为共享 execution truth。',
      '把这个 runtime artifact 作为 workspace、report、billing、print 层的基底契约。',
      'foundation 稳定后，才继续进入租户特定 overlay。',
    ],
  },
  {
    id: 'warehouse-planner',
    label: 'Warehouse Planner',
    labelZh: '仓库计划工作台',
    patternLayer: 'specific',
    summary: 'Show the tenant-specific operational overlay that sits on top of the shared warehouse foundation runtime.',
    summaryZh: '演示建立在共享 warehouse foundation runtime 之上的租户特定运营 overlay。',
    archetypePresetId: 'warehouse',
    builderArtifactType: 'bundle',
    builderPrompt: 'Build a tenant-specific warehouse planner app on top of the shared warehouse foundation runtime with inbound queue, putaway board, customer drill-down, exception escalation, blocked-bin supervisor approval, and mobile scan actions. Treat semantic model, report truth, billing truth, and printable truth as foundation artifacts rather than embedding them in this overlay.',
    builderPromptZh: '基于共享 warehouse foundation runtime 构建一个租户特定的仓库计划应用，包含入库队列、上架看板、客户下钻、异常升级、blocked bin 主管审批和移动扫码动作。语义模型、报表真相、计费真相和打印真相应视为 foundation artifacts，而不是写死在这个 overlay 里。',
    publishName: 'Warehouse Planner',
    publishNameZh: '仓库计划工作台',
    outcome: 'A WMS-specific overlay package that proves tenant operations can move fast without redefining warehouse truth each time.',
    outcomeZh: '一个 WMS 特定 overlay package，用来证明租户运营层可以快速迭代，而不用每次重新定义 warehouse truth。',
    buildsOn: 'warehouse-foundation-runtime',
    demoSteps: [
      'Start from the published warehouse foundation runtime.',
      'Apply the warehouse overlay blueprint and review operational surfaces.',
      'Use guided editing to verify workspaces, entities, approvals, and mobile scan flow.',
      'Generate the warehouse planner bundle and validate runtime bindings against the foundation layer.',
    ],
    demoStepsZh: [
      '先从已发布的 warehouse foundation runtime 开始。',
      '应用 warehouse overlay 蓝图并检查运营 surface。',
      '用 guided 编辑确认 workspace、entity、审批边界和移动扫码流。',
      '在 Builder 中生成 warehouse planner bundle，并验证它是建立在 foundation layer 之上的 runtime binding。',
    ],
  },
  {
    id: 'finance-foundation',
    label: 'Finance Foundation',
    labelZh: '财务基础底座',
    patternLayer: 'foundation',
    summary: 'Demonstrate finance as a business foundation package that other verticals can plug into.',
    summaryZh: '演示财务如何作为业务基础底座 package，被其他 vertical 直接接入。',
    archetypePresetId: 'finance_core',
    builderArtifactType: 'bundle',
    builderPrompt: 'Build a finance foundation workspace with AR/AP control room, anomaly watchlist, approval inbox, month-end checklist, and document print center.',
    builderPromptZh: '构建一个财务基础工作台，包含 AR/AP 控制室、异常观察列表、审批收件箱、月结清单和单证打印中心。',
    publishName: 'Finance Foundation',
    publishNameZh: '财务基础底座',
    outcome: 'A reusable finance package that proves shared foundation modules do not need to be rebuilt tenant by tenant.',
    outcomeZh: '一个可复用的财务 package，用来证明共享 foundation module 不需要每个租户重复重建。',
    demoSteps: [
      'Start with the finance foundation advisor preset.',
      'Review human boundary and risk signals for approvals and posting.',
      'Use Builder to generate the finance workspace bundle.',
      'Share the release to Exchange as an official foundation package.',
    ],
    demoStepsZh: [
      '从 finance foundation 顾问预设开始。',
      '检查审批与过账相关的人机边界和风险信号。',
      '用 Builder 生成 finance workspace bundle。',
      '把 release 分享到 Exchange，作为官方 foundation package。',
    ],
  },
];

export function getBusinessPackageShowcase(id: string | null | undefined): BusinessPackageShowcase | null {
  if (!id) return null;
  return BUSINESS_PACKAGE_SHOWCASES.find((item) => item.id === id) ?? null;
}
